import { useState, useMemo, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  key: keyof T;
  direction: SortDirection;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface UseTableFeaturesOptions<T> {
  data: T[] | undefined;
  searchFields?: (keyof T)[];
  defaultSort?: SortConfig<T>;
  initialFilters?: Record<string, string>;
}

export interface UseTableFeaturesReturn<T> {
  // Processed data
  processedData: T[];

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Sorting
  sortConfig: SortConfig<T> | null;
  requestSort: (key: keyof T) => void;
  getSortDirection: (key: keyof T) => SortDirection | null;

  // Filtering
  activeFilters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  clearAll: () => void;
}

function getNestedValue<T>(obj: T, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

function compareValues(a: unknown, b: unknown, direction: SortDirection): number {
  // Handle null/undefined - always sort to end
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc'
      ? a.getTime() - b.getTime()
      : b.getTime() - a.getTime();
  }

  // Try to parse as dates if they look like ISO strings
  if (typeof a === 'string' && typeof b === 'string') {
    const dateA = Date.parse(a);
    const dateB = Date.parse(b);
    if (!isNaN(dateA) && !isNaN(dateB) && a.includes('-') && b.includes('-')) {
      return direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
  }

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }

  // Handle strings
  const strA = String(a).toLowerCase();
  const strB = String(b).toLowerCase();

  if (direction === 'asc') {
    return strA.localeCompare(strB);
  }
  return strB.localeCompare(strA);
}

export function useTableFeatures<T extends object>({
  data,
  searchFields = [],
  defaultSort,
  initialFilters = {},
}: UseTableFeaturesOptions<T>): UseTableFeaturesReturn<T> {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(defaultSort || null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(initialFilters);

  // Request sort on a column
  const requestSort = useCallback((key: keyof T) => {
    setSortConfig(current => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      // Third click clears sort
      return null;
    });
  }, []);

  // Get current sort direction for a column
  const getSortDirection = useCallback((key: keyof T): SortDirection | null => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction;
  }, [sortConfig]);

  // Set a filter value
  const setFilter = useCallback((key: string, value: string) => {
    setActiveFilters(current => {
      if (!value || value === 'all') {
        const { [key]: _, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: value };
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  // Clear everything (search, sort, filters)
  const clearAll = useCallback(() => {
    setSearchQuery('');
    setSortConfig(defaultSort || null);
    setActiveFilters({});
  }, [defaultSort]);

  // Process data: filter → search → sort
  const processedData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // Apply search
    if (searchQuery && searchFields.length > 0) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => {
        return searchFields.some(field => {
          const value = getNestedValue(item, String(field));
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    // Apply filters (external filtering logic is handled by the component)
    // This hook provides filter state management, actual filtering
    // can be done in the component for complex filter logic

    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = getNestedValue(a, String(sortConfig.key));
        const bValue = getNestedValue(b, String(sortConfig.key));
        return compareValues(aValue, bValue, sortConfig.direction);
      });
    }

    return result;
  }, [data, searchQuery, searchFields, sortConfig]);

  return {
    processedData,
    searchQuery,
    setSearchQuery,
    sortConfig,
    requestSort,
    getSortDirection,
    activeFilters,
    setFilter,
    clearFilters,
    clearAll,
  };
}
